import type { InvoiceDetail, InvoiceSummary } from "@dishour/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unwrap } from "./client.js";

export type InvoiceScope = "restaurant" | "supplier";

const basePath = (scope: InvoiceScope) => (scope === "supplier" ? "/api/supplier/invoices" : "/api/invoices");

const get = <T,>(path: string) => fetch(path, { credentials: "same-origin" }).then(unwrap<T>);
const post = <T,>(path: string, body?: unknown) =>
  fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).then(unwrap<T>);

export const invoicesKey = (scope: InvoiceScope) => ["invoices", scope] as const;

export const useInvoices = (scope: InvoiceScope) =>
  useQuery({ queryKey: invoicesKey(scope), queryFn: () => get<InvoiceSummary[]>(basePath(scope)) });

export const useInvoice = (scope: InvoiceScope, id: string) =>
  useQuery({ queryKey: ["invoice", scope, id], queryFn: () => get<InvoiceDetail>(`${basePath(scope)}/${id}`) });

/** Outstanding balance total. Summed as integer cents to eliminate display rounding error. */
export function invoiceTotals(list: InvoiceSummary[]): { count: number; unpaidTotal: string } {
  const cents = list
    .filter((inv) => inv.paidAt === null)
    .reduce((sum, inv) => sum + Math.round(Number(inv.total) * 100), 0);
  return { count: list.length, unpaidTotal: (cents / 100).toFixed(2) };
}

export function useMarkPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; paid: boolean }) =>
      post<{ id: string; paidAt: string | null }>(
        `/api/supplier/invoices/${vars.id}/${vars.paid ? "mark-paid" : "unmark-paid"}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoicesKey("supplier") });
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
    },
  });
}

function useFinalize(kind: "rounds" | "deals") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; lines: unknown[] }) =>
      post<{ invoiceIds: string[] }>(`/api/supplier/${kind}/${vars.id}/finalize`, { lines: vars.lines }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier"] });
      queryClient.invalidateQueries({ queryKey: invoicesKey("supplier") });
    },
  });
}

export const useFinalizeRound = () => useFinalize("rounds");
export const useFinalizeDeal = () => useFinalize("deals");
